import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

/**
 * Visual style of a page-header action button.
 * - `primary`  -> filled (mat-flat-button color="primary")
 * - `stroked`  -> outlined (mat-stroked-button)
 * - `icon`     -> icon-only (mat-icon-button)
 */
export type PageHeaderActionVariant = 'primary' | 'stroked' | 'icon';

export interface PageHeaderAction {
  /** Stable id used for trackBy. Optional. */
  id?: string;
  /**
   * Button label. Optional for `icon` variant. Pass a function for labels
   * that change over time (e.g. a selection count).
   */
  label?: string | (() => string);
  /**
   * Hide the button. Pass a function for visibility that changes over time
   * (e.g. only show when rows are selected).
   */
  hidden?: boolean | (() => boolean);
  /** Material icon name. */
  icon?: string;
  /** Visual style. Defaults to `primary`. */
  variant?: PageHeaderActionVariant;
  /**
   * Permission slug(s) gating the button via `*appHasPermission`.
   * When omitted the button is always shown.
   */
  permission?: string | string[];
  /** Permission match mode when `permission` is an array. Defaults to `any`. */
  permissionMode?: 'any' | 'all';
  /**
   * Disable the button. Pass a function for state that changes over time
   * (e.g. form validity) — it is re-evaluated on every change-detection pass.
   */
  disabled?: boolean | (() => boolean);
  /** Tooltip text. */
  tooltip?: string;
  /** Click handler. */
  handler: () => void;
}

export interface PageHeaderConfig {
  /** Page title rendered in the toolbar. */
  title: string;
  /** Optional Material icon shown before the title. */
  icon?: string;
  /** Optional small badge (e.g. a count) shown after the title. */
  badge?: string | number | null;
  /**
   * Back-button target (router commands). When omitted (and no
   * {@link backHandler} is set) the back button is hidden.
   */
  backRoute?: string | unknown[] | null;
  /**
   * Back-button click handler. Use for pages that navigate via a method
   * (e.g. `location.back()`). Ignored when {@link backRoute} is provided.
   */
  backHandler?: () => void;
  /** Tooltip for the back button. Defaults to "Back". */
  backTooltip?: string;
  /** Action buttons rendered on the right of the toolbar. */
  actions?: PageHeaderAction[];
}

/**
 * Global store for the page header (back button, icon, title and action
 * buttons) rendered by the main layout's toolbar. Feature pages call
 * {@link set} in `ngOnInit` and {@link clear} in `ngOnDestroy`.
 */
@Injectable({ providedIn: 'root' })
export class PageHeaderService {
  private readonly configSubject = new BehaviorSubject<PageHeaderConfig | null>(
    null,
  );

  /** Current page-header configuration (null when no page sets one). */
  readonly config$: Observable<PageHeaderConfig | null> =
    this.configSubject.asObservable();

  /** Replace the current page-header configuration. */
  set(config: PageHeaderConfig | null): void {
    this.configSubject.next(config);
  }

  /** Merge a partial update into the current configuration. */
  patch(partial: Partial<PageHeaderConfig>): void {
    const current = this.configSubject.value;
    this.configSubject.next({ title: '', ...current, ...partial });
  }

  /** Clear the page header (call in `ngOnDestroy`). */
  clear(): void {
    this.configSubject.next(null);
  }
}
